/**
 * @file components/home/home-data.ts
 * @description 公開首頁的展示資料。
 *
 * 集中存放兩組資料：
 *   1. CASE_STORIES — 客戶案例故事（3 張大卡）
 *   2. FIELD_GALLERY — 現場稽核畫廊（8 格 4×2）
 *
 * 圖片放在 apps/web/public/home/，由 Vite 直接以根路徑 `/home/*.jpg` 提供。
 * 案例卡的 CASE 編號 / 方案 chip、現場卡的場景名 + 地點，已分別烘焙在圖片裡（左下／右下／右上角）；
 * 元件層因此不再額外疊加同樣的 caption，避免重複。
 *
 * 未來接入真實資料時：
 *   - 案例故事：改從 `clients` 表 + 額外的 `client_showcases` 表撈取，由運維端控制是否公開。
 *   - 現場畫廊：替換 imageSrc 與 alt 文案即可。
 */

/** 案例故事大卡資料結構 */
export interface CaseStory {
  /** 案例編號（mono 字體展示用，亦烘焙於圖片右上角） */
  code: string;
  /** 方案標籤（含碼 + 中文，如 "TOP · 有機"） */
  scheme: string;
  /** 方案 chip 文字色（CSS 變數） */
  schemeColor: string;
  /** 方案 chip 背景色（CSS 變數） */
  schemeBg: string;
  /** 客戶名 */
  client: string;
  /** 地點（縣市 · 鄉鎮） */
  location: string;
  /** 引言（單句） */
  quote: string;
  /** metric 數值（短，可含單位） */
  metric: string;
  /** metric 說明 */
  metricLabel: string;
  /** 案例照片相對路徑（public 目錄根） */
  imageSrc: string;
  /** 圖片無障礙描述 */
  imageAlt: string;
}

/** 現場畫廊單格資料結構 */
export interface FieldGalleryEntry {
  /** 場景名稱（中文），亦烘焙於圖片左下角 */
  label: string;
  /** 副標（地點，mono 字體），亦烘焙於圖片右下角 */
  sub: string;
  /** 場景照片相對路徑（public 目錄根） */
  imageSrc: string;
  /** 圖片無障礙描述 */
  imageAlt: string;
}

/**
 * 三張案例故事：覆蓋三套主要驗證方案各一例。
 * 排列順序刻意為 TOP / TAP / ECO，呼應方案區塊的順序。
 */
export const CASE_STORIES: readonly CaseStory[] = [
  {
    code: 'CASE-001',
    scheme: 'TOP · 有機',
    schemeColor: 'var(--forest-700)',
    schemeBg: 'var(--forest-50)',
    client: '清水有機米農場',
    location: '臺中 · 清水',
    quote: '從育苗到收成，每一道工序都有完整可追溯的紀錄。',
    metric: '24 ha',
    metricLabel: '驗證面積',
    imageSrc: '/home/case-001.jpg',
    imageAlt: '臺中清水有機米農場稻田全景',
  },
  {
    code: 'CASE-002',
    scheme: 'TAP · 產銷履歷',
    schemeColor: 'var(--stone-700)',
    schemeBg: 'var(--stone-100)',
    client: '南投青果合作社',
    location: '南投 · 信義',
    quote: '產銷履歷讓我們的高山茶葉直接進入日本連鎖通路。',
    metric: '162',
    metricLabel: '批次／年',
    imageSrc: '/home/case-002.jpg',
    imageAlt: '南投信義高山茶園採摘現場',
  },
  {
    code: 'CASE-003',
    scheme: 'ECO · 友善',
    schemeColor: 'var(--forest-700)',
    schemeBg: 'var(--forest-100)',
    client: '東岸友善果園',
    location: '花蓮 · 壽豐',
    quote: '友善農法讓土地恢復生機，蜜蜂也回來了。',
    metric: '7 年',
    metricLabel: '連續驗證',
    imageSrc: '/home/case-003.jpg',
    imageAlt: '花蓮壽豐友善果園柑橘樹列',
  },
] as const;

/**
 * 八格現場畫廊：依稽核作業流程排列
 *   田間 → 抽樣 → 加工 → 包裝 → 巡查 → 文件 → 委員會 → 證書
 * 4 列 × 2 行剛好填滿。
 *
 * 色板輪換策略：相鄰格子用不同色板，避免重複；同一行內 forest / forestLight / stone 交替。
 */
export const FIELD_GALLERY: readonly FieldGalleryEntry[] = [
  {
    label: '田間稽核',
    sub: '嘉義 · 中埔',
    imageSrc: '/home/field-01-inspect.jpg',
    imageAlt: '稽核員於稻田蹲點以平板紀錄',
  },
  {
    label: '抽樣作業',
    sub: '屏東 · 萬丹',
    imageSrc: '/home/field-02-sampling.jpg',
    imageAlt: '抽樣人員於分裝倉清點蔬果',
  },
  {
    label: '加工廠審查',
    sub: '彰化 · 員林',
    imageSrc: '/home/field-03-warehouse.jpg',
    imageAlt: '稽核員於加工廠檢視蔬菜批次',
  },
  {
    label: '包裝場稽核',
    sub: '雲林 · 斗六',
    imageSrc: '/home/field-04-packaging.jpg',
    imageAlt: '包裝場堆高機與紙箱貨架',
  },
  {
    label: '田間巡查',
    sub: '臺東 · 池上',
    imageSrc: '/home/field-05-patrol.jpg',
    imageAlt: '兩位巡查員於田間以紙夾紀錄',
  },
  {
    label: '文件審查',
    sub: '臺北 · 內湖',
    imageSrc: '/home/field-06-docs.jpg',
    imageAlt: '審查員於桌面翻閱稽核文件',
  },
  {
    label: '委員會審議',
    sub: '臺北 · 總部',
    imageSrc: '/home/field-07-committee.jpg',
    imageAlt: '驗證委員會於會議室審議案件',
  },
  {
    label: '證書頒發',
    sub: '臺北 · 總部',
    imageSrc: '/home/field-08-certificate.jpg',
    imageAlt: 'ZHCERT 證書封面特寫',
  },
] as const;
